import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Gamepad2, Shield, Zap, Globe, Wifi, Target, Rocket, Lock, Users,
  Building2, Server, Briefcase, FileCheck, Network, KeyRound, Eye,
  CheckCircle, ArrowRight, MapPin, Gauge, ShieldCheck, AlertTriangle,
  PlayCircle, Tv,
} from "lucide-react";
import { getGamingPage } from "../../data/gamingPages.js";
import { getBusinessPage } from "../../data/businessPages.js";
import { getStreamingPage } from "../../data/streamingPages.js";

const ICONS = {
  Gamepad2, Shield, Zap, Globe, Wifi, Target, Rocket, Lock, Users,
  Building2, Server, Briefcase, FileCheck, Network, KeyRound, Eye,
  CheckCircle, MapPin, Gauge, ShieldCheck, PlayCircle, Tv,
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};
const stagger = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };

const CONFIG = {
  gaming: {
    label: "Gaming VPN",
    lookup: getGamingPage,
    badgeIcon: Gamepad2,
    heroBg: "bg-gradient-to-br from-purple-900 via-gray-900 to-brand-900",
    blobA: "bg-purple-500/20",
    blobB: "bg-brand-500/20",
    badge: "bg-purple-500/20 border border-purple-500/30 text-purple-300",
    gradText: "bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent",
    iconGrad: "bg-gradient-to-br from-purple-600 to-pink-600",
    btnGrad: "bg-gradient-to-r from-purple-600 to-pink-600 shadow-purple-500/25",
    check: "text-purple-400",
    cardHover: "hover:border-purple-500/30",
    cardBorder: "border-purple-500/10",
    ctaBg: "bg-gradient-to-br from-purple-900 via-gray-900 to-pink-900",
    stats: [
      { big: "WireGuard", small: "Low Overhead" },
      { big: "Hidden", small: "Your Real IP" },
      { big: "US", small: "Server Network" },
    ],
    painHeading: (n) => `Common ${n} connection headaches`,
    painSub: "The problems a VPN actually addresses, minus the hype.",
    benefitsHeading: "Why gamers use SACVPN",
    benefits: [
      { icon: "Zap", title: "Low Overhead", desc: "The WireGuard protocol is built for speed and adds little latency. How much you add depends on your distance to the server you pick." },
      { icon: "Eye", title: "Hide Your Real IP", desc: "Your traffic exits from our server, so your home IP stays off the table in lobbies, customs, and on stream." },
      { icon: "Globe", title: "Reach Any Region", desc: "Connect to game servers worldwide to play with friends abroad or find a cleaner route to a distant data center." },
      { icon: "Wifi", title: "Bypass ISP Throttling", desc: "If your ISP quietly slows game traffic at peak hours, an encrypted tunnel keeps it from singling your games out." },
    ],
    noteHeading: "Which server should I pick?",
    coverageHeading: "Plays on your platforms",
    ctaHeading: "Ready to level up your connection?",
    ctaSub: "Hide your real IP and reach game servers worldwide with SACVPN. Start your 14-day free trial. No credit card required.",
  },
  business: {
    label: "Business VPN",
    lookup: getBusinessPage,
    badgeIcon: Briefcase,
    heroBg: "bg-gradient-to-br from-brand-900 via-gray-900 to-gray-950",
    blobA: "bg-brand-500/20",
    blobB: "bg-brand-400/10",
    badge: "bg-brand-500/20 border border-brand-500/30 text-brand-300",
    gradText: "bg-gradient-to-r from-brand-300 to-brand-500 bg-clip-text text-transparent",
    iconGrad: "bg-gradient-to-br from-brand-600 to-brand-500",
    btnGrad: "bg-gradient-to-r from-brand-600 to-brand-500 shadow-brand-500/25",
    check: "text-brand-400",
    cardHover: "hover:border-brand-500/30",
    cardBorder: "border-brand-500/10",
    ctaBg: "bg-gradient-to-br from-brand-900 via-gray-900 to-gray-900",
    stats: [
      { big: "WireGuard", small: "Fast & Modern" },
      { big: "No Logs", small: "Privacy First" },
      { big: "Team-Ready", small: "Every Device" },
    ],
    painHeading: (n) => `What ${n} teams are up against`,
    painSub: "The everyday security and access gaps a business VPN closes.",
    benefitsHeading: "Why businesses run SACVPN",
    benefits: [
      { icon: "Lock", title: "Encrypt Every Connection", desc: "WireGuard encrypts your team's traffic on any network, so client data stays private on hotel, cafe, and home Wi-Fi." },
      { icon: "Server", title: "Secure Remote Access", desc: "Give staff a protected path to the tools and files they need from anywhere, without exposing them to the open internet." },
      { icon: "KeyRound", title: "Consistent Egress IP", desc: "Route through a stable server IP you can allowlist in your business tools, so only your team reaches sensitive dashboards." },
      { icon: "ShieldCheck", title: "Hide Your Office IP", desc: "Keep your real network address private, cutting the reconnaissance an attacker would start with." },
    ],
    noteHeading: "How to roll it out",
    coverageHeading: "Covers every device your team uses",
    ctaHeading: "Lock down your team's connections",
    ctaSub: "Give your business encrypted, private access on every network with SACVPN. Start your 14-day free trial. No credit card required.",
  },
  streaming: {
    label: "Streaming VPN",
    lookup: getStreamingPage,
    badgeIcon: PlayCircle,
    heroBg: "bg-gradient-to-br from-cyan-900 via-gray-900 to-brand-900",
    blobA: "bg-cyan-500/20",
    blobB: "bg-brand-500/20",
    badge: "bg-cyan-500/20 border border-cyan-500/30 text-cyan-300",
    gradText: "bg-gradient-to-r from-cyan-300 to-brand-400 bg-clip-text text-transparent",
    iconGrad: "bg-gradient-to-br from-cyan-600 to-brand-600",
    btnGrad: "bg-gradient-to-r from-cyan-600 to-brand-600 shadow-cyan-500/25",
    check: "text-cyan-400",
    cardHover: "hover:border-cyan-500/30",
    cardBorder: "border-cyan-500/10",
    ctaBg: "bg-gradient-to-br from-cyan-900 via-gray-900 to-brand-900",
    stats: [
      { big: "WireGuard", small: "Fast & Modern" },
      { big: "Encrypted", small: "On Any Wi-Fi" },
      { big: "No Logs", small: "Privacy First" },
    ],
    painHeading: (n) => `What to know about ${n} and a VPN`,
    painSub: "The honest picture: privacy is guaranteed, catalog access is up to the service.",
    benefitsHeading: "Why people run SACVPN for streaming",
    benefits: [
      { icon: "Lock", title: "Private On Any Wi-Fi", desc: "WireGuard encrypts your streaming on hotel, airport, and cafe networks, so your account and what you watch stay private." },
      { icon: "Globe", title: "Change Your Apparent Region", desc: "Route through a server in the region you pick to change your apparent location. Whether a given catalog loads is up to the service's terms." },
      { icon: "Wifi", title: "Bypass ISP Throttling", desc: "If your ISP quietly slows video at peak hours, an encrypted tunnel keeps it from singling your streaming out." },
      { icon: "ShieldCheck", title: "No-Logs, Honest Framing", desc: "We do not log your activity, and we never promise a service always works. Use your own account and follow its terms." },
    ],
    noteHeading: "Which server should I pick?",
    coverageHeading: "Streams on your devices",
    ctaHeading: "Stream privately on any network",
    ctaSub: "Encrypt your streaming and route through the region you choose with SACVPN. Start your 14-day free trial. No credit card required.",
  },
};

export default function SegmentVPN({ segment }) {
  const { slug } = useParams();
  const cfg = CONFIG[segment];
  const entry = cfg ? cfg.lookup(slug) : null;

  if (!entry) {
    const backTo = segment === "business"
      ? "/industries/small-business"
      : segment === "streaming"
        ? "/personal"
        : "/gaming";
    const backLabel = segment === "business"
      ? "business"
      : segment === "streaming"
        ? "streaming"
        : "gaming";
    return (
      <section className="py-32 text-center">
        <div className="container-xl">
          <h1 className="text-3xl font-display font-bold text-white mb-4">Page not found</h1>
          <Link to={backTo} className="text-brand-400 hover:underline">
            Back to {backLabel} VPN
          </Link>
        </div>
      </section>
    );
  }

  const url = `https://www.sacvpn.com/${segment}/${entry.slug}`;
  const title = segment === "gaming"
    ? `${entry.name} VPN | Hide Your IP & Reach Any Region | SACVPN`
    : segment === "streaming"
      ? `${entry.name} VPN | Private Streaming on Any Network | SACVPN`
      : `${entry.name} VPN | Secure Business VPN for ${entry.name} | SACVPN`;
  const description = entry.intro.length > 158 ? entry.intro.slice(0, 155).trim() + "..." : entry.intro;
  const BadgeIcon = cfg.badgeIcon;

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entry.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `${cfg.label} for ${entry.name}`,
    name: `SACVPN ${cfg.label}`,
    description,
    provider: { "@type": "Organization", name: "SACVPN" },
    areaServed: { "@type": "Country", name: "Worldwide" },
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={entry.keywords} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className={`absolute inset-0 ${cfg.heroBg}`} />
        <div className={`absolute top-0 right-0 w-96 h-96 ${cfg.blobA} rounded-full blur-3xl`} />
        <div className={`absolute bottom-0 left-0 w-96 h-96 ${cfg.blobB} rounded-full blur-3xl`} />
        <div className="container-xl relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeInUp} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${cfg.badge} text-sm font-medium mb-6`}>
              <BadgeIcon className="w-4 h-4" />
              <span>{cfg.label}{entry.genre ? ` · ${entry.genre}` : entry.sector ? ` · ${entry.sector}` : entry.service ? ` · ${entry.service}` : ""}</span>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight">
              {entry.name} VPN.{" "}
              <span className={cfg.gradText}>{entry.tagline}.</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-gray-300 mb-8">
              {entry.intro}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link to="/download" className={`inline-flex items-center gap-2 px-8 py-4 rounded-xl ${cfg.btnGrad} text-white font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all`}>
                <Rocket className="w-5 h-5" /> Download Free
              </Link>
              <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-white/20 text-white font-bold hover:bg-white/10 transition-all">
                View Pricing <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-8 mt-10 pt-8 border-t border-white/10">
              {cfg.stats.map((s, i) => (
                <div key={i}>
                  <div className="text-2xl md:text-3xl font-bold text-white">{s.big}</div>
                  <div className="text-sm text-gray-400">{s.small}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pain points */}
      <section className="py-20 bg-gray-900">
        <div className="container-xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14 max-w-2xl mx-auto">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-display font-bold text-white mb-4">{cfg.painHeading(entry.name)}</motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-400">{cfg.painSub}</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {entry.painPoints.map((p, i) => (
              <motion.div key={i} variants={fadeInUp} className={`bg-gray-800/40 rounded-2xl p-7 border ${cfg.cardBorder} ${cfg.cardHover} transition-colors`}>
                <div className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center mb-4">
                  <AlertTriangle className={`w-5 h-5 ${cfg.check}`} />
                </div>
                <h3 className="font-bold text-white mb-2">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-950">
        <div className="container-xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-display font-bold text-white mb-4">{cfg.benefitsHeading}</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cfg.benefits.map((b, i) => {
              const Icon = ICONS[b.icon] || Shield;
              return (
                <motion.div key={i} variants={fadeInUp} className="bg-gray-800/30 rounded-2xl p-7 border border-gray-700/50 hover:bg-gray-800/50 transition-all">
                  <div className={`w-12 h-12 rounded-xl ${cfg.iconGrad} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{b.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{b.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Note + coverage */}
      <section className="py-20 bg-gray-900">
        <div className="container-xl grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="text-3xl font-display font-bold text-white mb-4">{cfg.noteHeading}</motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-400 leading-relaxed mb-6">{entry.serverNote}</motion.p>
            <motion.div variants={fadeInUp} className="flex items-center gap-3 text-gray-300">
              <MapPin className={`w-5 h-5 ${cfg.check}`} />
              <span>3 US nodes in Texas, Virginia, and Dallas, one tap to switch.</span>
            </motion.div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className={`bg-gray-800/50 rounded-3xl p-8 border ${cfg.cardBorder}`}>
            <h3 className="text-xl font-bold text-white mb-4">{cfg.coverageHeading}</h3>
            <div className="grid grid-cols-2 gap-3">
              {(entry.platforms || ["Windows", "macOS", "iOS", "Android", "Linux", "Routers"]).map((pf, i) => (
                <div key={i} className="flex items-center gap-3 text-gray-200">
                  <CheckCircle className={`w-5 h-5 ${cfg.check} flex-shrink-0`} />
                  <span>{pf}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-950">
        <div className="container-xl max-w-3xl">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-3xl md:text-4xl font-display font-bold text-white mb-10 text-center">
            {entry.name} VPN questions, answered
          </motion.h2>
          <div className="space-y-4">
            {entry.faqs.map((f, i) => (
              <details key={i} className="group bg-gray-800/40 rounded-2xl border border-gray-700/50 p-6">
                <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-white">
                  <span>{f.q}</span>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <p className="text-gray-400 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Parent industry guide (business segment only, when the vertical has one) */}
      {entry.industryGuide && (
        <section className="py-10 bg-gray-950 border-t border-gray-800/60">
          <div className="container-xl text-center">
            <Link
              to={entry.industryGuide.to}
              className={`inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors`}
            >
              <span>Also see the broader {entry.industryGuide.label}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Related */}
      {entry.related && entry.related.length > 0 && (
        <section className="py-16 bg-gray-900">
          <div className="container-xl">
            <h2 className="text-2xl font-display font-bold text-white mb-8 text-center">More SACVPN guides</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {entry.related.map((rslug, i) => {
                const rel = cfg.lookup(rslug);
                if (!rel) return null;
                return (
                  <Link key={i} to={`/${segment}/${rslug}`} className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-800/60 border ${cfg.cardBorder} ${cfg.cardHover} text-gray-200 hover:text-white transition-all`}>
                    {rel.name} VPN <ArrowRight className="w-4 h-4" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className={`py-20 ${cfg.ctaBg} relative overflow-hidden`}>
        <div className="container-xl relative z-10 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-display font-bold text-white mb-6">{cfg.ctaHeading}</motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">{cfg.ctaSub}</motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/download" className={`inline-flex items-center gap-2 px-8 py-4 rounded-xl ${cfg.btnGrad} text-white font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all`}>
                <Rocket className="w-5 h-5" /> Download Now
              </Link>
              <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-white/30 text-white font-bold hover:bg-white/10 transition-all">
                View Pricing <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
